import React, { useEffect, useState } from 'react';
import moment from 'moment';
import getJwtPayload from '../../utils/methods/getJwtPayload';
import { withTranslation } from '../Translator';
import Button from '../Button';
import Stack from '../Stack';
import Text from '../Text';
import { UnorderedList } from '../UnorderedList';
import { ModalView } from '../ModalView';
import getContactLink from '../../utils/methods/getContactLink';
import getCheckoutLink from '../../utils/methods/getCheckoutLink';

const RENEW_LICENSE_MODAL_CLOSED_KEY = 'pp_renew_license_modal';
const FIRST_THRESHOLD_DAYS_TO_LICENSE_EXPIRATION = 30;
const SECOND_THRESHOLD_DAYS_TO_LICENSE_EXPIRATION = 5;

const getClosureThreshold = () => parseInt(localStorage.getItem(RENEW_LICENSE_MODAL_CLOSED_KEY), 10) || null;
const setClosureThreshold = threshold => localStorage.setItem(RENEW_LICENSE_MODAL_CLOSED_KEY, threshold);
const clearClosureThreshold = () => localStorage.removeItem(RENEW_LICENSE_MODAL_CLOSED_KEY);

const calculateDaysDifference = (end) => {
  const now = moment().utc().startOf('day');
  const expires = moment(end).utc().startOf('day');
  const difference = expires.diff(now, 'days');

  return difference;
};

const getCurrentThreshold = (daysLeft) => {
  if (daysLeft > FIRST_THRESHOLD_DAYS_TO_LICENSE_EXPIRATION) {
    clearClosureThreshold();

    return null;
  }

  return daysLeft <= SECOND_THRESHOLD_DAYS_TO_LICENSE_EXPIRATION
    ? SECOND_THRESHOLD_DAYS_TO_LICENSE_EXPIRATION
    : FIRST_THRESHOLD_DAYS_TO_LICENSE_EXPIRATION;
};

const RenewLicenseModal = ({
  jwt,
  userEmail,
  language,
  t,
  licenseType,
  trafficLimit,
  expirationDate,
}) => {
  const isCorePlus = licenseType === 'core' && trafficLimit > 500000;
  if (!isCorePlus) return null;

  const organization = getJwtPayload(jwt).org;
  const userLanguage = language.split('-')[0];
  const expiresIn = parseInt(calculateDaysDifference(new Date(expirationDate)), 10);

  const closureThreshold = getClosureThreshold();
  const currentThreshold = getCurrentThreshold(expiresIn);
  const [showModal, setShowModal] = useState(false);

  useEffect(
    () => {
      setShowModal(closureThreshold !== currentThreshold);
    }, [closureThreshold, currentThreshold],
  );

  if (!showModal) return null;

  const content = (
    <>
      <Text>{t('RenewLicenseModal.no-fallback.description')}</Text>
      <UnorderedList>
        <UnorderedList.Item>{t('RenewLicenseModal.no-fallback.bullet-0')}</UnorderedList.Item>
        <UnorderedList.Item>{t('RenewLicenseModal.no-fallback.bullet-1')}</UnorderedList.Item>
      </UnorderedList>
    </>
  );

  const actions = (
    <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
      <Stack.Item fill>
        <Button
          text={t('RenewLicenseModal.button.contact')}
          onClick={() => {
            window.open(
              getContactLink({ language: userLanguage, organization, email: userEmail, topic: 'renewLicense' }),
              '_blank',
            );
          }}
        />
      </Stack.Item>
      <Stack.Item>
        <Button
          text={t('RenewLicenseModal.button.close')}
          onClick={() => {
            setShowModal(false);
            setClosureThreshold(currentThreshold);
          }}
        />
      </Stack.Item>
      <Stack.Item>
        <Button
          appearance="primary"
          text={t('RenewLicenseModal.button.renew')}
          onClick={() => { window.open(getCheckoutLink({ organization, language: userLanguage }), '_blank'); }}
        />
      </Stack.Item>
    </Stack>
  );

  const modalTitle = expiresIn >= 0 ? t('RenewLicenseModal.modal.title', { count: expiresIn }) : t('RenewLicenseModal.modal.title-after-expiration');

  return (
    <ModalView
      title={modalTitle}
      actions={actions}
    >
      {content}
    </ModalView>
  );
};

export default withTranslation('components')(RenewLicenseModal);
